import { API_PATHS } from "@shared/platform/app-metadata";
import { DASHBOARD_AREA_KEYS, dashboardEndpointKey, type DashboardAreaKey } from "@shared/dashboard/dashboard-areas";
import { DASHBOARD_COPY } from "@shared/dashboard/dashboard-copy";
import { dashboardRangeQuerySchema } from "@shared/dashboard/dashboard-data";
import { DASHBOARD_SUMMARIZERS, type DashboardAreaInputs } from "@shared/dashboard/metrics";

type Row = Record<string, unknown>;

export type DemoDashboardSources = {
  chemicals: Row[];
  transactions: Row[];
  orders: Row[];
  contacts: Row[];
  contactPeople: Row[];
  users: Row[];
  auditHistory: Row[];
  orderHistory: Map<number, Row[]>;
};

const AREA_BY_PATH: Record<string, DashboardAreaKey> = Object.fromEntries(
  DASHBOARD_AREA_KEYS.map((area) => [API_PATHS[dashboardEndpointKey(area)], area]),
);

function activity(sources: DemoDashboardSources) {
  const orderEvents = Array.from(sources.orderHistory.values()).flat().map((row) => ({ ...row, recordedAt: row.recordedAt ?? row.eventDate }));
  return { changes: sources.auditHistory, orderEvents } as unknown as DashboardAreaInputs["audit"];
}

function demoInputs(sources: DemoDashboardSources): DashboardAreaInputs {
  const orders = sources.orders.map((order) => ({ ...order, paidTotal: order.paidTotal ?? 0 }));
  return {
    inventory: { chemicals: sources.chemicals, documents: [] },
    transactions: { transactions: sources.transactions },
    orders: { orders },
    contacts: { contacts: sources.contacts, people: sources.contactPeople, orders },
    audit: activity(sources),
    users: { ...activity(sources), users: sources.users },
  } as unknown as DashboardAreaInputs;
}

export function demoDashboardResponse(url: URL, sources: DemoDashboardSources): { status: number; body: unknown } | null {
  const area = AREA_BY_PATH[url.pathname];
  if (!area) return null;
  const query = dashboardRangeQuerySchema.safeParse(Object.fromEntries(url.searchParams));
  if (!query.success) return { status: 400, body: { message: DASHBOARD_COPY.invalidPeriod } };
  const summarize = DASHBOARD_SUMMARIZERS[area] as (input: unknown, range: { start: string; endExclusive: string }) => unknown;
  return { status: 200, body: summarize(demoInputs(sources)[area], { start: query.data.start, endExclusive: query.data.end }) };
}
